import axios from 'axios';
import { api_labelflow_training } from '../../../config.jsx';

/**
 * The Prediction screen's HTTP layer.
 *
 * WHAT THESE REPLACED. Until 2026-09-21 this file talked to
 * `/prediction/models/register`, `/prediction/models/{id}` and
 * `/prediction/predict/{id}` — a registry the app never wrote to, and a
 * predict route that returned `random.random()` per class. Every one of those
 * routes is gone; see `2_training_api/interface/i_prediction_management.py`.
 *
 * `validateStatus: () => true` on both calls, deliberately. The API answers a
 * real reason on 400, 404 and 413 — "this version has not finished training",
 * "the trained checkpoint is not on this server's disk" — and an axios throw
 * collapses all of them into one catch whose only honest rendering is a generic
 * failure. That is exactly how the old login reported every outcome as "wrong
 * email or password". No path here carries a trailing slash (see
 * `.claude/memory/labelflow-api-path-rules.md`).
 */

const BASE = `${api_labelflow_training}/prediction`;

/** The envelope both routes answer with, normalised so callers never branch on axios internals. */
const unwrap = (response) => {
  const payload = response?.data;
  if (payload && typeof payload === 'object' && payload.success === true) {
    return { ok: true, data: payload.data, message: payload.message || '' };
  }
  const message =
    (payload && typeof payload === 'object' && payload.message) ||
    (typeof payload === 'string' && payload) ||
    `Request failed (${response?.status ?? 'no response'})`;
  return { ok: false, data: null, message };
};

/** Every trained version this owner can run. */
export const fetchPredictableModels = async (ownerId, modelType) => {
  try {
    const response = await axios.get(`${BASE}/models`, {
      params: modelType ? { owner_id: ownerId, model_type: modelType } : { owner_id: ownerId },
      validateStatus: () => true,
    });
    return unwrap(response);
  } catch (error) {
    return { ok: false, data: null, message: error?.message || 'Could not reach the training API.' };
  }
};

/**
 * Run one version against one image.
 *
 * `signal` is threaded through so a queue of images can be stopped mid-run —
 * without it, pressing Stop on a forty-image folder leaves every remaining
 * request in flight and the results keep landing after the user gave up.
 */
export const runPrediction = async ({ modelType, versionId, file, conf, signal }) => {
  const form = new FormData();
  form.append('model_type', modelType);
  form.append('version_id', versionId);
  form.append('conf', String(conf));
  form.append('file', file);

  try {
    const response = await axios.post(`${BASE}/predict`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      validateStatus: () => true,
      signal,
    });
    return unwrap(response);
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      return { ok: false, data: null, message: '', canceled: true };
    }
    return { ok: false, data: null, message: error?.message || 'Could not reach the training API.' };
  }
};
